import { motion } from 'framer-motion';
import Critter from '../glyphs/Critter';
import type { Plank } from '../../hooks/useWeeklyJourney';

const DAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const W = 320;
const L = 34;
const R = 286;
const BASE = 78;
const ARCH = 24;

// Sunday sits on the right bank, so the week reads the way Hebrew does.
const plankAt = (i: number) => {
  const t = (i + 0.5) / 7;
  return { x: R - t * (R - L), y: BASE - ARCH * Math.sin(Math.PI * t) };
};
const archY = (x: number) => BASE - ARCH * Math.sin((Math.PI * (R - x)) / (R - L));
const curve = (lift: number) => {
  const pts = Array.from({ length: 25 }, (_, k) => {
    const x = R - (k / 24) * (R - L);
    return `${x.toFixed(1)},${(archY(x) - lift).toFixed(1)}`;
  });
  return `M${pts.join(' L')}`;
};

interface Props {
  planks: Plank[];
  /** Current week: show the critter standing on today's plank. */
  avatarId?: string;
  today?: number;
  labels?: boolean;
  className?: string;
  title?: string;
}

/** One week drawn as one bridge. Empty days are gaps in the planks, nothing more. */
export default function WeekBridge({ planks, avatarId, today, labels, className, title }: Props) {
  const h = labels ? 118 : 104;
  const top = avatarId ? 8 : 34;
  return (
    <svg viewBox={`0 ${top} ${W} ${h - top}`} className={className} role="img" aria-label={title}>
      {/* water */}
      <path d={`M0 ${BASE + 10} Q80 ${BASE + 4} 160 ${BASE + 10} T320 ${BASE + 10} V${h} H0Z`} fill="hsl(var(--primary) / 0.10)" />
      <path d={`M14 ${BASE + 20} q10 -4 20 0 M120 ${BASE + 18} q10 -4 20 0 M230 ${BASE + 22} q10 -4 20 0`} stroke="hsl(var(--primary) / 0.25)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* banks */}
      <path d={`M0 ${BASE - 6} Q22 ${BASE - 10} ${L + 8} ${BASE + 2} L${L + 8} ${BASE + 14} L0 ${BASE + 14}Z`} fill="hsl(var(--primary) / 0.55)" />
      <path d={`M${W} ${BASE - 6} Q${W - 22} ${BASE - 10} ${R - 8} ${BASE + 2} L${R - 8} ${BASE + 14} L${W} ${BASE + 14}Z`} fill="hsl(var(--primary) / 0.55)" />
      {/* rope rails */}
      <path d={curve(13)} fill="none" stroke="hsl(var(--bridge-foreground) / 0.35)" strokeWidth="1.5" strokeLinecap="round" />
      {[L + 6, 104, 160, 216, R - 6].map((x) => (
        <line key={x} x1={x} x2={x} y1={archY(x) - 13} y2={archY(x)} stroke="hsl(var(--bridge-foreground) / 0.3)" strokeWidth="1.5" />
      ))}
      <path d={curve(-5)} fill="none" stroke="hsl(var(--bridge-foreground) / 0.4)" strokeWidth="2.5" strokeLinecap="round" />

      {planks.map((p, i) => {
        const { x, y } = plankAt(i);
        const angle = (Math.atan2(archY(x + 1) - archY(x - 1), 2) * 180) / Math.PI;
        const common = { x: -15, y: -4, width: 30, height: 8, rx: 2.5 };
        return (
          <g key={i} transform={`translate(${x} ${y}) rotate(${angle})`}>
            {p === 'laid' && (
              <motion.rect
                {...common}
                fill="hsl(var(--bridge))"
                stroke="hsl(var(--bridge-foreground) / 0.45)"
                strokeWidth="1"
                initial={{ scaleY: 0, opacity: 0 }}
                animate={{ scaleY: 1, opacity: 1 }}
                transition={{ delay: 0.05 * i, type: 'spring', stiffness: 260, damping: 18 }}
              />
            )}
            {p === 'touched' && (
              <rect {...common} fill="hsl(var(--bridge-soft))" stroke="hsl(var(--bridge) / 0.8)" strokeWidth="1.2" />
            )}
            {(p === 'empty' || p === 'future') && (
              <rect {...common} fill="none" stroke="hsl(var(--muted-foreground) / 0.35)" strokeWidth="1" strokeDasharray={p === 'future' ? '1.5 3' : '3 3'} />
            )}
          </g>
        );
      })}

      {labels && planks.map((_, i) => {
        const { x } = plankAt(i);
        return (
          <text key={i} x={x} y={h - 4} textAnchor="middle" fontSize="11" fontWeight={i === today ? 800 : 500}
            fill={i === today ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))'}>
            {DAYS[i]}
          </text>
        );
      })}

      {avatarId && today !== undefined && (() => {
        const { x, y } = plankAt(today);
        return (
          <g transform={`translate(${x} ${y - 6})`}>
            <g className="critter-breathe">
              <Critter id={avatarId} size={40} x={-20} y={-38} blink />
            </g>
          </g>
        );
      })()}
    </svg>
  );
}
